// ============================================
// VOLUNTEER MODULE - API SERVICE
// ALL backend calls live here.
// No page should import axios directly.
// To change the server URL, edit .env only.
// ============================================

import axios from "axios";

export const BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5000";

export const API = axios.create({
  baseURL: `${BASE_URL}/api`,
  headers: { "Content-Type": "application/json" },
});

// ── AUTH TOKEN INTERCEPTORS ────────────────
// Auto-attach JWT to every request from localStorage
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("vh_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// 401 → token is invalid/expired. Clear it and notify the app
// to bounce back to login. Avoid infinite loops by ignoring auth endpoints.
//
// Special case: requests with config.silent401 = true skip the global
// handler. This lets the boot logic check the stored token without
// firing a misleading "session expired" message when SSO is also in flight.
API.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err.response?.status;
    const url    = err.config?.url || "";
    const silent = err.config?.silent401 === true;
    if (status === 401
        && !silent
        && !url.includes("/auth/login")
        && !url.includes("/auth/forgot-password")
        && !url.includes("/auth/sso-verify")) {
      localStorage.removeItem("vh_token");
      window.dispatchEvent(new Event("vh:auth-expired"));
    }
    return Promise.reject(err);
  }
);

// ── AUTH ENDPOINTS ─────────────────────────
export const apiLogin           = (email, password)    => API.post("/auth/login", { email, password });
export const apiGetMe           = ()                   => API.get("/auth/me");
// Silent variant: 401 from this call won't trigger the global "session expired"
// banner. Use during boot when SSO may still be racing.
export const apiGetMeSilent     = ()                   => API.get("/auth/me", { silent401: true });
export const apiForgotPassword  = (email)              => API.post("/auth/forgot-password", { email });
export const apiResetPassword   = (token, password)    => API.post("/auth/reset-password", { token, password });
export const apiVerifyInvite    = (token)              => API.get(`/auth/invite/${token}`);
export const apiAcceptInvite    = (token, name, password) => API.post("/auth/accept-invite", { token, name, password });
export const apiSsoVerify       = (encryptedData)      => API.post("/auth/sso-verify", { encryptedData });

// ── USER MANAGEMENT (admin only) ───────────
export const getUsers           = ()                   => API.get("/users");
export const inviteUser         = (email, role)        => API.post("/users/invite", { email, role });
export const updateUser         = (id, data)           => API.patch(`/users/${id}`, data);
export const deleteUser         = (id)                 => API.delete(`/users/${id}`);
export const resendInvite       = (id)                 => API.post(`/users/${id}/resend-invite`);

// ── STATS ──────────────────────────────────
export const getStats = () =>
  API.get("/stats");

// ── EVENTS ─────────────────────────────────
export const getEvents = () =>
  API.get("/events/all");

export const createEvent = (data) =>
  API.post("/events/create", data);

export const updateEvent = (id, data) =>
  API.put(`/events/${id}`, data);

export const deleteEvent = (id) =>
  API.delete(`/events/${id}`);

// ── VENUES ─────────────────────────────────
export const getVenues = () =>
  API.get("/venues/all");

export const getVenuesByEvent = (eventId) =>
  API.get(`/venues/event/${eventId}`);

export const createVenue = (data) =>
  API.post("/venues/create", data);

export const updateVenue = (id, data) =>
  API.put(`/venues/${id}`, data);

export const deleteVenue = (id) =>
  API.delete(`/venues/${id}`);

// ── GATES ───────────────────────────────────
export const getGates = () =>
  API.get("/gates");

export const getGatesByVenue = (venueId) =>
  API.get(`/gates/venue/${venueId}`);

export const createGate = (data) =>
  API.post("/gates/create", data);

export const updateGate = (id, data) =>
  API.put(`/gates/${id}`, data);

export const deleteGate = (id) =>
  API.delete(`/gates/${id}`);

// ── VOLUNTEERS ─────────────────────────────
export const getVolunteers = () =>
  API.get("/volunteers/all");

export const createVolunteer = (data) =>
  API.post("/volunteers/create", data);

export const updateVolunteer = (id, data) =>
  API.put(`/volunteers/${id}`, data);

// Partial update — arrival date, departure date, status only
export const patchVolunteer = (id, data) =>
  API.patch(`/volunteers/${id}`, data);

export const deleteVolunteer = (id) =>
  API.delete(`/volunteers/${id}`);

export const importVolunteers = (formData) =>
  API.post("/volunteers/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const downloadVolunteerTemplate = () =>
  window.open(`${BASE_URL}/api/volunteers/template`, "_blank");

// ── DUTIES ─────────────────────────────────
export const getDuties = (eventId) =>
  API.get(`/duties/event/${eventId}`);

export const createDuty = (data) =>
  API.post("/duties/create", data);

export const updateDuty = (id, data) =>
  API.put(`/duties/${id}`, data);

export const deleteDuty = (id) =>
  API.delete(`/duties/${id}`);

// ── SESSIONS ───────────────────────────────
export const getSessions = () =>
  API.get("/sessions");

// ── ASSIGNMENTS ────────────────────────────
export const getVolunteersForDate = (date) =>
  API.get(`/assignments/date/${date}`);

export const saveAssignment = (data) =>
  API.post("/assignments", data);

// ── WHATSAPP ───────────────────────────────
export const sendWhatsAppToVolunteer = (volunteerId) =>
  API.post("/whatsapp/send-to-volunteer", { volunteerId });

export const sendWhatsAppBulk = (date) =>
  API.post("/whatsapp/bulk", { date });

// ── ATTENDANCE ─────────────────────────────
export const importAttendance = (formData) =>
  API.post("/attendance/import", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

export const getAttendanceForDate = (date) =>
  API.get(`/attendance/date/${date}`);

export const downloadAttendanceTemplate = () =>
  window.open(`${BASE_URL}/api/attendance/template`, "_blank");

// ── REPORTS ────────────────────────────────
export const downloadReport = (entity) =>
  window.open(`${BASE_URL}/api/reports/download?entity=${entity}`, "_blank");

export const generateReport = (params) =>
  API.get("/reports", { params });

// ── ASSIGNMENTS ─────────────────────────────
export const getAssignmentsByEvent = (eventId) =>
  API.get(`/assignments/event/${eventId}`);

// ── GHL CONTACTS ────────────────────────────
export const getGHLContacts = (params) =>
  API.get("/ghl/contacts", { params });

export const bulkAddFromGHL = (data) =>
  API.post("/ghl/contacts/bulk-add", data);

// ── SMS ─────────────────────────────────────
export const sendSMSToVolunteer = (volunteerId) =>
  API.post("/sms/send-to-volunteer", { volunteerId });

export const sendSMSBulk = (date) =>
  API.post("/sms/bulk", { date });

// ── REPORT DATA (contextual, relational) ────
export const getReportVolunteersByEvent   = (id, params) => API.get(`/reports/data/volunteers-by-event/${id}`,  { params });
export const getReportVolunteersByJamiat  = (id, params) => API.get(`/reports/data/volunteers-by-jamiat/${id}`, { params });
export const getReportAssignmentsByEvent  = (id, params) => API.get(`/reports/data/assignments-by-event/${id}`, { params });
export const getReportAssignmentsByVenue  = (id, params) => API.get(`/reports/data/assignments-by-venue/${id}`, { params });
export const getReportAssignmentsByGate   = (id, params) => API.get(`/reports/data/assignments-by-gate/${id}`,  { params });
export const getReportAttendanceByEvent   = (id, params) => API.get(`/reports/data/attendance-by-event/${id}`,  { params });
export const getReportAttendanceByDate    = (params)     => API.get(`/reports/data/attendance-by-date`,          { params });
export const getReportEventsByStatus      = (id, params) => API.get(`/reports/data/events-by-status/${id}`,      { params });
export const getReportEventsByDate        = (params)     => API.get(`/reports/data/events-by-date`,              { params });
export const getReportGateFill            = (id)         => API.get(`/reports/data/gate-fill/${id}`);

// ── CONTEXTUAL REPORTS ───────────────────────
export const downloadContextualReport = (params) => {
  const qs = Object.entries(params).filter(([,v])=>v).map(([k,v])=>`${k}=${encodeURIComponent(v)}`).join("&");
  window.open(`${BASE_URL}/api/reports/download-contextual?${qs}`, "_blank");
};

export const getReportData = (type, id, params={}) =>
  API.get(`/reports/data/${type}${id?"/"+id:""}`, { params });